import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';

import { Button, Dialog, DialogContent, DialogTitle, IconButton, TextField, Checkbox, ListItemText, Chip, TableContainer, Table, TableHead, TableBody, TableCell, TableRow, Paper, Tooltip as MuiTooltip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DatePicker } from '@material-ui/pickers';

import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip } from 'recharts';
import { MdOutlineClose, MdShowChart, MdOutlineTableChart, MdBarChart } from 'react-icons/md';
import format from 'date-fns/format';
import { sub } from 'date-fns';

import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import defaultLabelStyle from 'app/admin/common/component/DefaultLabelStyle';

const intervalButtons = [{ label: 'All', unit: null }, { label: '1y', unit: { years: 1 } }, { label: '1m', unit: { months: 1 } }, { label: '1w', unit: { weeks: 1 } }, { label: '1d', unit: { days: 1 } }];
const MAX_DEFAULT = 25;

function isNumber(x) { return typeof x === 'number' && Number.isFinite(x); }
function toMs(v) { if (!v) return NaN; if (v instanceof Date) return v.getTime(); const t = new Date(v).getTime(); return Number.isFinite(t) ? t : NaN; }
function normalizeNumber(v) { if (isNumber(v)) return v; if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v); if (typeof v === 'boolean') return v ? 1 : 0; return null; }
function colorForIndex(idx, count) { if (count <= 1) return '#252f3e'; return 'hsl(' + ((idx * 60) % 360) + ', 70%, 40%)'; }

function pickUom(uomMap, key) {
    if (!uomMap || !key) return null;
    const root = String(key).includes('.') ? String(key).split('.')[0] : null;
    if (typeof uomMap.get === 'function' && typeof uomMap.has === 'function') {
        if (uomMap.has(key)) return uomMap.get(key) ?? null;
        if (root && uomMap.has(root)) return uomMap.get(root) ?? null;
        return null;
    }
    if (Object.prototype.hasOwnProperty.call(uomMap, key)) return uomMap[key];
    if (root && Object.prototype.hasOwnProperty.call(uomMap, root)) return uomMap[root];
    return null;
}

function buildMinMaxDates(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return { minDate: null, maxDate: null };
    const sorted = [...rows].filter(Boolean).filter(r => r && r.ts).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    if (!sorted.length) return { minDate: null, maxDate: null };
    const minDate = new Date(sorted[0].ts), maxDate = new Date(sorted[sorted.length - 1].ts);
    if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) return { minDate: null, maxDate: null };
    return { minDate, maxDate };
}

function getSeriesDisplayKey(key, valueType) {
    return key + '__' + valueType;
}

function shouldUsePercentageDisplay(metric, percentageDisplayKeys, key, valueType) {
    if (metric === 'ape') return false;
    return !!percentageDisplayKeys[getSeriesDisplayKey(key, valueType)];
}

function getDisplayValue(rawValue, metric, percentageDisplayKeys, key, valueType) {
    if (rawValue == null || Number.isNaN(rawValue)) return null;
    if (shouldUsePercentageDisplay(metric, percentageDisplayKeys, key, valueType)) return rawValue * 100;
    return rawValue;
}

export default function ModelValidationParamHistoryPopup(props) {
    const mainTheme = useSelector(selectMainTheme);
    const { open, onClose, title, logDataList, availableKeys, uomMap, defaultSelectedKeys } = props;

    const [isChartView, setIsChartView] = useState(true);
    const [chartType, setChartType] = useState('line');
    const [metric, setMetric] = useState('value');

    const sortedRows = useMemo(() => {
        const list = Array.isArray(logDataList) ? logDataList : [];
        return [...list].filter(r => r && r.ts).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    }, [logDataList]);

    const allKeys = useMemo(() => (Array.isArray(availableKeys) ? availableKeys.filter(Boolean) : []).slice().sort((a, b) => a.localeCompare(b, 'hu')), [availableKeys]);
    const { minDate, maxDate } = useMemo(() => buildMinMaxDates(sortedRows), [sortedRows]);

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [percentageDisplayKeys, setPercentageDisplayKeys] = useState({});

    useEffect(() => {
        if (!open) return;
        setIsChartView(true);
        setChartType('line');
        setMetric('value');
        if (minDate && maxDate) { setDateFrom(minDate); setDateTo(maxDate); } else { setDateFrom(null); setDateTo(null); }

        const seed = Array.isArray(defaultSelectedKeys) && defaultSelectedKeys.length ? defaultSelectedKeys.filter(Boolean) : (allKeys.length ? [allKeys[0]] : []);
        setSelectedKeys(Array.from(new Set(seed)).slice(0, MAX_DEFAULT));
        setPercentageDisplayKeys({});
    }, [open, defaultSelectedKeys, minDate, maxDate, allKeys]);

    useEffect(() => {
        setPercentageDisplayKeys(prev => {
            const next = {};

            selectedKeys.forEach((key) => {
                const trueKey = getSeriesDisplayKey(key, 'true');
                const estKey = getSeriesDisplayKey(key, 'est');
                const aeKey = getSeriesDisplayKey(key, 'ae');

                if (prev[trueKey]) next[trueKey] = true;
                if (prev[estKey]) next[estKey] = true;
                if (prev[aeKey]) next[aeKey] = true;
            });

            return next;
        });
    }, [selectedKeys]);

    const togglePercentageDisplay = useCallback((key, valueType) => {
        const displayKey = getSeriesDisplayKey(key, valueType);
        setPercentageDisplayKeys(prev => ({ ...prev, [displayKey]: !prev[displayKey] }));
    }, []);

    const handleIntervalSelect = useCallback((unit) => {
        if (!minDate || !maxDate) return;
        if (!unit) { setDateFrom(minDate); setDateTo(maxDate); return; }
        const newFrom = sub(maxDate, unit);
        if (newFrom < minDate) { setDateFrom(minDate); setDateTo(maxDate); return; }
        setDateFrom(newFrom); setDateTo(maxDate);
    }, [minDate, maxDate]);

    const isIntervalAvailable = useCallback((unit) => {
        if (!minDate || !maxDate) return false;
        if (!unit) return true;
        return sub(maxDate, unit) >= minDate;
    }, [minDate, maxDate]);

    const isDataAvailable = useMemo(() => selectedKeys.length > 0 && minDate && maxDate && dateFrom && dateTo, [selectedKeys, minDate, maxDate, dateFrom, dateTo]);

    const filteredData = useMemo(() => {
        if (!isDataAvailable) return [];
        const merged = {}, fromMs = toMs(dateFrom), toMsVal = toMs(dateTo);

        for (const row of sortedRows) {
            const t = toMs(row.ts);
            if (!Number.isFinite(t) || t < fromMs || t > toMsVal) continue;

            const ts = row.ts;
            if (!merged[ts]) merged[ts] = { ts };

            for (const key of selectedKeys) {
                const f = row?.features ? row.features[key] : undefined;
                merged[ts][key + '__true'] = normalizeNumber(f?.true_value);
                merged[ts][key + '__est'] = normalizeNumber(f?.estimated_value);
                merged[ts][key + '__ae'] = normalizeNumber(f?.ae);
                const ape = normalizeNumber(f?.ape);
                merged[ts][key + '__ape'] = ape == null ? null : ape * 100;
            }
        }

        return Object.values(merged).filter(Boolean).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    }, [sortedRows, selectedKeys, dateFrom, dateTo, isDataAvailable]);

    const chartData = useMemo(() => {
        return filteredData.map((row) => {
            const next = { ...row };

            selectedKeys.forEach((key) => {
                next[key + '__true_display'] = getDisplayValue(row[key + '__true'], metric, percentageDisplayKeys, key, 'true');
                next[key + '__est_display'] = getDisplayValue(row[key + '__est'], metric, percentageDisplayKeys, key, 'est');
                next[key + '__ae_display'] = getDisplayValue(row[key + '__ae'], metric, percentageDisplayKeys, key, 'ae');
                next[key + '__ape_display'] = row[key + '__ape'];
            });

            return next;
        });
    }, [filteredData, selectedKeys, metric, percentageDisplayKeys]);

    const getTooltip = useCallback(({ active, payload }) => {
        if (!(active && payload && payload.length)) return null;
        const p = payload[0] && payload[0].payload ? payload[0].payload : null;
        if (!p) return null;

        const d = new Date(p.ts);
        const tsLabel = Number.isNaN(d.getTime()) ? String(p.ts).slice(0, 10) : format(d, 'yyyy.MM.dd.');

        const fmtNum = (v) => (v == null || Number.isNaN(v)) ? '-' : formatLabelValue(v, defaultLabelStyle());
        const fmtPct = (v) => (v == null || Number.isNaN(v)) ? '-' : (formatLabelValue(v, { thousandsSeparator: false, decimalPlaces: 2 }) + '%');

        const renderRawAndPct = (raw, key, valueType, uom) => {
            if (!shouldUsePercentageDisplay(metric, percentageDisplayKeys, key, valueType)) {
                return fmtNum(raw) + (uom ? ' ' + uom : '');
            }

            const pctValue = raw == null || Number.isNaN(raw) ? null : raw * 100;

            return (
                <span>
                    <span>{fmtPct(pctValue)}</span>
                    <span className='opacity-70'> (raw: {fmtNum(raw)}{uom ? ' ' + uom : ''})</span>
                </span>
            );
        };

        return (
            <div className='flex flex-col gap-1 bg-black opacity-80 p-8 text-white text-xs shadow-lg min-w-[240px] max-w-[420px]'>
                <div className='mb-2 font-semibold text-[11px] opacity-80'>{tsLabel}</div>

                {selectedKeys.map((k) => {
                    if (metric === 'value') {
                        const u = pickUom(uomMap, k);
                        const tv = p[k + '__true'];
                        const ev = p[k + '__est'];

                        return (
                            <div key={k} className='font-medium'>
                                {k}: <span className='opacity-80'>true</span> {renderRawAndPct(tv, k, 'true', u)}, <span className='opacity-80'>est</span> {renderRawAndPct(ev, k, 'est', u)}
                            </div>
                        );
                    }

                    if (metric === 'ae') {
                        const u = pickUom(uomMap, k);
                        const raw = p[k + '__ae'];

                        return (
                            <div key={k} className='font-medium'>
                                {k}: {renderRawAndPct(raw, k, 'ae', u)}
                            </div>
                        );
                    }

                    return <div key={k} className='font-medium'>{k}: {fmtPct(p[k + '__ape'])}</div>;
                })}
            </div>
        );
    }, [selectedKeys, metric, uomMap, percentageDisplayKeys]);

    const IntervalButtons = () => (
        <div className='flex gap-4 items-center'>
            {intervalButtons.map(({ label, unit }) => (
                <MuiTooltip key={label} title={(unit ? 'Last ' : '') + label} arrow><span>
                    <IconButton size='small' onClick={() => handleIntervalSelect(unit)} disabled={!isIntervalAvailable(unit)}>
                        <span className='text-xs font-semibold p-4'>{label}</span>
                    </IconButton>
                </span></MuiTooltip>
            ))}

            <MuiTooltip title='Metric' arrow>
                <span className='flex items-center gap-2'>
                    <IconButton size='small' onClick={() => setMetric('value')}><span className={'text-xs font-semibold p-4 ' + (metric === 'value' ? 'opacity-100' : 'opacity-60')}>True/Est</span></IconButton>
                    <IconButton size='small' onClick={() => setMetric('ae')}><span className={'text-xs font-semibold p-4 ' + (metric === 'ae' ? 'opacity-100' : 'opacity-60')}>AE</span></IconButton>
                    <IconButton size='small' onClick={() => setMetric('ape')}><span className={'text-xs font-semibold p-4 ' + (metric === 'ape' ? 'opacity-100' : 'opacity-60')}>APE</span></IconButton>
                </span>
            </MuiTooltip>

            <MuiTooltip title={chartType === 'line' ? 'Bar chart' : 'Line chart'} arrow><span>
                <IconButton size='small' onClick={() => setChartType(t => (t === 'line' ? 'bar' : 'line'))} disabled={!isChartView}>
                    {chartType === 'line' ? <MdBarChart size={20} /> : <MdShowChart size={20} />}
                </IconButton>
            </span></MuiTooltip>

            <MuiTooltip title={isChartView ? 'Table view' : 'Chart view'} arrow><span>
                <IconButton size='small' onClick={() => setIsChartView(v => !v)}>
                    {isChartView ? <MdOutlineTableChart size={20} /> : <MdShowChart size={20} />}
                </IconButton>
            </span></MuiTooltip>
        </div>
    );

    const hasStableLayout = allKeys.length > 0;
    const pctToggleBaseClass = 'flex h-24 min-w-32 cursor-pointer items-center justify-center text-10 leading-none transition-colors px-8';

    function getPctToggleClass(enabled, extraClassName = '') {
        return (pctToggleBaseClass + ' ' + (enabled ? 'bg-grey-700 text-grey-100 hover:bg-grey-600' : 'bg-grey-300 text-grey-800 hover:bg-grey-400') + (extraClassName ? ' ' + extraClassName : ''));
    }

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open={!!open} onClose={onClose} fullWidth maxWidth={hasStableLayout ? 'lg' : 'sm'}>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                 
                    <div className={'flex items-center justify-between gap-32 px-32 ' + (hasStableLayout ? 'border-b pb-14' : '')}>
                        <div className='whitespace-nowrap'>{title || 'Parameter Timeseries'}</div>
                        <div className='flex items-center gap-8'>
                            <Button onClick={onClose}>Close</Button>
                            <IconButton size='small' onClick={onClose}><MdOutlineClose size={20} /></IconButton>
                        </div>
                    </div>

                    {hasStableLayout && (
                        <div className='w-full flex flex-col gap-8 shadow-inner px-20 pt-14'>
                            <div className='w-full flex flex-col gap-8 rounded-xl bg-grey-100 py-10 shadow px-16'>
                                <Autocomplete
                                    className='flex-1'
                                    multiple
                                    options={allKeys}
                                    value={selectedKeys}
                                    onChange={(e, newValue) => setSelectedKeys(Array.isArray(newValue) ? newValue.slice(0, MAX_DEFAULT) : [])}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option}
                                    renderOption={(option, { selected }) => {
                                        const parts = String(option).split('.');
                                        const parent = parts.length > 1 ? parts[0] : null;
                                        const child = parts.length > 1 ? parts.slice(1).join('.') : option;
                                        return (<><Checkbox style={{ marginRight: 8 }} checked={selected} /><ListItemText primary={parent ? (<span><span className='font-semibold opacity-30'>{parent + '.'}</span>{child}</span>) : option} /></>);
                                    }}
                                    renderTags={(value, getTagProps) => {
                                        const count = value.length;

                                        return value.map((option, index) => {
                                            const col = colorForIndex(index, count);
                                            const isTruePctEnabled = !!percentageDisplayKeys[getSeriesDisplayKey(option, 'true')];
                                            const isEstPctEnabled = !!percentageDisplayKeys[getSeriesDisplayKey(option, 'est')];
                                            const tagProps = getTagProps({ index });

                                            return (
                                                <div key={option + '_' + index} className='flex items-center'>

                                                    <Chip {...tagProps} size='small' label={option} style={{ padding: '5px 2px 5px 5px', fontSize: '12px', border: '2px solid ' + col, zIndex: 10 }} />

                                                    {metric === 'value' && (
                                                        <div className='flex -ml-12 overflow-hidden rounded-r-full' onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>

                                                            <MuiTooltip title='Display true as percentage' arrow>
                                                                <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePercentageDisplay(option, 'true'); }} className={getPctToggleClass(isTruePctEnabled, 'pl-16 pr-8')}>T%</span>
                                                            </MuiTooltip>

                                                            <MuiTooltip title='Display estimated as percentage' arrow>
                                                                <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePercentageDisplay(option, 'est'); }} className={getPctToggleClass(isEstPctEnabled, 'pr-10 rounded-r-full border-l border-black/10')}>E%</span>
                                                            </MuiTooltip>

                                                        </div>
                                                    )}

                                                </div>
                                            );
                                        });
                                    }}
                                    renderInput={(params) => {
                                        const { InputProps: extractedInputProps, inputProps: extractedInnerInputProps, ...filteredParams } = params;
                                        return (
                                            <TextField
                                                {...filteredParams}
                                                variant='outlined'
                                                label='Search and select'
                                                placeholder='Select keys'
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{ ...extractedInputProps, style: { minHeight: 40 }, inputProps: { ...extractedInnerInputProps, style: { padding: '10px 8px', fontSize: '13px', lineHeight: 1.5 } } }}
                                            />
                                        );
                                    }}
                                />

                                <div className='flex-1 flex items-center gap-16'>
                                    <DatePick className='flex-1' label='From' value={dateFrom} minDate={minDate} maxDate={maxDate} onChange={(v) => setDateFrom(v)} />
                                    <DatePick className='flex-1' label='To' value={dateTo} minDate={dateFrom || minDate} maxDate={maxDate} onChange={(v) => setDateTo(v)} />
                                    <IntervalButtons />
                                </div>
                            </div>
                        </div>
                    )}
                </DialogTitle>

                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: hasStableLayout ? '520px' : 'auto' }}>
                    {allKeys.length === 0 && <div className='text-center'>No keys available.</div>}
                    {allKeys.length > 0 && selectedKeys.length === 0 && <div className='text-center'>Select at least one feature.</div>}
                    {allKeys.length > 0 && selectedKeys.length > 0 && filteredData.length === 0 && <div className='text-center'>No data available.</div>}

                    {filteredData.length > 0 && (
                        <div className='w-full flex-1' style={{ minHeight: 400 }}>
                            {isChartView ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                    {chartType === 'bar' ? (
                                        <BarChart data={chartData} margin={{ top: 10, left: -15, right: 10, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                            <YAxis orientation='left' width={80} tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                            <ReTooltip content={getTooltip} cursor={{ fill: 'transparent' }} wrapperStyle={{ position: 'absolute', zIndex: 1000 }} />

                                            {metric === 'value' && selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return (
                                                    <React.Fragment key={key}>
                                                        <Bar dataKey={key + '__true_display'} name={key + ' true'} fill={col} isAnimationActive={false} />
                                                        <Bar dataKey={key + '__est_display'} name={key + ' est'} fill={col} fillOpacity={0.35} isAnimationActive={false} />
                                                    </React.Fragment>
                                                );
                                            })}

                                            {metric === 'ae' && selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return <Bar key={key} dataKey={key + '__ae_display'} name={key} fill={col} isAnimationActive={false} />;
                                            })}

                                            {metric === 'ape' && selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return <Bar key={key} dataKey={key + '__ape_display'} name={key} fill={col} isAnimationActive={false} />;
                                            })}
                                        </BarChart>
                                    ) : (
                                        <LineChart data={chartData} margin={{ top: 10, left: -15, right: 10, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                            <YAxis orientation='left' width={80} tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                            <ReTooltip content={getTooltip} cursor={{ fill: 'transparent' }} wrapperStyle={{ position: 'absolute', zIndex: 1000 }} />

                                            {metric === 'value' && selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return (
                                                    <React.Fragment key={key}>
                                                        <Line type='monotone' dataKey={key + '__true_display'} name={key + ' true'} stroke={col} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />
                                                        <Line type='monotone' dataKey={key + '__est_display'} name={key + ' est'} stroke={col} strokeWidth={2} dot={false} strokeDasharray='4 2' activeDot={{ r: 3 }} />
                                                    </React.Fragment>
                                                );
                                            })}

                                            {metric === 'ae' && selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return <Line key={key} type='monotone' dataKey={key + '__ae_display'} name={key} stroke={col} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />;
                                            })}

                                            {metric === 'ape' && selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return <Line key={key} type='monotone' dataKey={key + '__ape_display'} name={key} stroke={col} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />;
                                            })}
                                        </LineChart>
                                    )}
                                </ResponsiveContainer>
                            ) : (
                                <DataTable data={filteredData} selectedKeys={selectedKeys} metric={metric} percentageDisplayKeys={percentageDisplayKeys} />
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}

function DataTable({ data, selectedKeys, metric, percentageDisplayKeys }) {
    const fmtNum = (v) => (v == null || Number.isNaN(v)) ? '-' : formatLabelValue(v, defaultLabelStyle());
    const fmtPct = (v) => (v == null || Number.isNaN(v)) ? '-' : (formatLabelValue(v, { thousandsSeparator: false, decimalPlaces: 2 }) + '%');

    const headerCells = useMemo(() => {
        if (metric === 'value') {
            const out = [];
            selectedKeys.forEach((k) => {
                out.push({ key: k + '__true', label: k + ' true', sourceKey: k, valueType: 'true' });
                out.push({ key: k + '__est', label: k + ' est', sourceKey: k, valueType: 'est' });
            });
            return out;
        }

        return selectedKeys.map((k) => ({
            key: k + (metric === 'ae' ? '__ae' : '__ape'),
            label: k,
            sourceKey: k,
            valueType: metric === 'ae' ? 'ae' : 'ape'
        }));
    }, [selectedKeys, metric]);

    return (
        <TableContainer component={Paper} style={{ height: '100%' }}>
            <Table className='w-full shadow-2xl' size='small'>
                <TableHead><TableRow>
                    <TableCell className='text-xs font-semibold'>Timestamp</TableCell>
                    {headerCells.map(h => (<TableCell key={h.key} align='right' className='text-xs font-semibold whitespace-nowrap'>{h.label}</TableCell>))}
                </TableRow></TableHead>

                <TableBody>
                    {data.map((row, idx) => (
                        <TableRow key={idx}>
                            <TableCell className='whitespace-nowrap text-xs py-12'>{format(new Date(row.ts), 'yyyy.MM.dd.')}</TableCell>
                            {headerCells.map(h => {
                                const raw = row[h.key];
                                const displayValue = getDisplayValue(raw, metric, percentageDisplayKeys, h.sourceKey, h.valueType);

                                if (metric === 'ape') return <TableCell key={h.key} align='right' className='whitespace-nowrap text-xs tabular-nums'>{fmtPct(displayValue)}</TableCell>;
                                if (shouldUsePercentageDisplay(metric, percentageDisplayKeys, h.sourceKey, h.valueType)) return <TableCell key={h.key} align='right' className='whitespace-nowrap text-xs tabular-nums'>{fmtPct(displayValue)}</TableCell>;
                                return <TableCell key={h.key} align='right' className='whitespace-nowrap text-xs tabular-nums'>{fmtNum(displayValue)}</TableCell>;
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function DatePick(props) {
    return (
        <DatePicker
            className={props.className}
            label={props.label}
            margin='dense'
            value={props.value ?? undefined}
            onChange={e => props.onChange(e)}
            ampm={false}
            minDate={props.minDate}
            maxDate={props.maxDate}
            format='yyyy MMMM d.'
            cancelLabel='Back'
            okLabel='Ok'
            clearLabel='Clear'
            inputVariant='outlined'
            clearable
        />
    );
}